import { useState } from 'react';
import { useMutation } from '@tanstack/react-query';
import { useSnackbar } from 'notistack';
import {
  Alert,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableRow,
} from '@mui/material';
import PriceCheckOutlinedIcon from '@mui/icons-material/PriceCheckOutlined';
import { repriceAir } from '../../api/endpoints/air';
import type { ReservationReportRow } from '../../api/types/reports';
import type { ShoppingCart } from '../../api/types/shoppingCart';
import { ApiErrorAlert } from '../ApiErrorAlert';

/**
 * "Yeniden Fiyatla" — `Air/RePrice` for a still-held (`Prebooked`) Air reservation. The
 * endpoint re-prices the PNR against the current fare and returns the updated
 * `ShoppingCart`; it does not persist (NLog only), so this dialog just surfaces the new
 * totals next to the old ones for the operator to eyeball and acknowledge.
 *
 * `bookingData.locator` is the PNR / BookingCode; the full fetched cart is sent as
 * `shoppingCart` so the worker can resolve the provider. Shown only for the reservation
 * record state (see reservation/capabilities.ts `reprice`).
 */

const tl = (n: number | undefined): string =>
  n === undefined ? '—' : n.toLocaleString('tr-TR', { minimumFractionDigits: 2, maximumFractionDigits: 2 });

/** `ShoppingCart.shoppingSummary` isn't strongly typed (the aggregate carries an index
 *  signature) — narrow just the two totals we render. */
function cartTotals(cart: ShoppingCart | null | undefined): { grandTotal?: number; remaining?: number } {
  if (!cart) return {};
  const summary = cart.shoppingSummary as { grandTotal?: number; remainingTotal?: number } | undefined;
  return {
    grandTotal: summary?.grandTotal,
    remaining: summary?.remainingTotal ?? cart.remainingTotal,
  };
}

export function RePriceAction({
  row,
  cart,
}: {
  row: ReservationReportRow;
  /** The `GetShoppingCart` result the detail page already fetched for this reservation. */
  cart?: ShoppingCart;
}) {
  const { enqueueSnackbar } = useSnackbar();
  const [result, setResult] = useState<ShoppingCart | null>(null);

  const locator = row.pnr || row.documentNumber || '';

  const mutation = useMutation({
    mutationFn: () =>
      repriceAir({
        bookingData: { locator },
        shoppingCart: cart,
      }),
    onSuccess: (res) => {
      setResult(res.shoppingCart ?? null);
    },
    onError: (err) =>
      enqueueSnackbar(err instanceof Error ? err.message : 'Yeniden fiyatlama başarısız.', { variant: 'error' }),
  });

  const oldTotal = row.totalFare;
  const next = cartTotals(result);
  const diff = next.grandTotal !== undefined && oldTotal !== undefined ? next.grandTotal - oldTotal : undefined;

  return (
    <>
      <Button
        size="small"
        variant="outlined"
        startIcon={<PriceCheckOutlinedIcon />}
        disabled={!locator || mutation.isPending}
        onClick={() => mutation.mutate()}
      >
        {mutation.isPending ? 'Fiyatlanıyor…' : 'Yeniden Fiyatla'}
      </Button>

      <Dialog open={!!result} onClose={() => setResult(null)} maxWidth="xs" fullWidth>
        <DialogTitle>Güncel Fiyat{row.pnr ? ` — ${row.pnr}` : ''}</DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ mt: 1 }}>
            <Alert severity={diff && Math.abs(diff) >= 0.01 ? 'warning' : 'info'}>
              {diff && Math.abs(diff) >= 0.01
                ? `Fiyat ${diff > 0 ? 'arttı' : 'düştü'} (${tl(Math.abs(diff))} ₺). Yeni tutarı onaylayın.`
                : 'Fiyat değişmedi.'}
            </Alert>
            <Table size="small">
              <TableBody>
                <TableRow>
                  <TableCell>Önceki toplam</TableCell>
                  <TableCell align="right">{tl(oldTotal)} ₺</TableCell>
                </TableRow>
                <TableRow>
                  <TableCell>Yeni toplam</TableCell>
                  <TableCell align="right">{tl(next.grandTotal)} ₺</TableCell>
                </TableRow>
                <TableRow>
                  <TableCell>Kalan ödeme</TableCell>
                  <TableCell align="right">{tl(next.remaining)} ₺</TableCell>
                </TableRow>
              </TableBody>
            </Table>
            <ApiErrorAlert error={mutation.error} />
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setResult(null)}>Vazgeç</Button>
          <Button
            variant="contained"
            onClick={() => {
              enqueueSnackbar('Yeni fiyat onaylandı.', { variant: 'success' });
              setResult(null);
            }}
          >
            Onayla
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
}
